import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSettingsStore } from '@/stores/settingsStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SearchResultItem } from './search-result-item';
import { AttendeeSearchResult } from '../types/attendee-search-result';
import { useAttendeeFilterResult } from '../api/get-attendee-fllter-result';
import { Skeleton } from '@/components/ui/skeleton';
import { getLabelValue } from '@/utils/getLabelValue';
import { Search } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePassage } from '@/hooks/use-passage';
import { SortAttendees } from '@/components/sort-attendees';
import _ from 'lodash';
import { useAttendeeSearchResult } from '../api/get-attendee-search-result';

interface FilterByStatDialogProps {
  children: React.ReactNode;
  status: 'notArrived' | 'arrived' | 'exited' | 'booked' | 'onsite' | 'usedTickets';
}

export function AttendeeFilterDialog({ children, status }: FilterByStatDialogProps) {
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [isOpened, setIsOpened] = useState(false);

  const [sortBy, setSortBy] = useState<'firstName' | 'lastName' | 'companyName' | 'email'>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const eventId = useSettingsStore(state => state.eventId) ?? 0;
  const breakoutId = useSettingsStore(state => state.breakoutId) ?? null;

  const { pass, proccessingBadgeNo } = usePassage();

  const debouncedSetSearchKey = useMemo(() => _.debounce((value: string) => setDebouncedSearchKey(value), 300), []);

  useEffect(() => {
    return () => {
      debouncedSetSearchKey.cancel();
    };
  }, [debouncedSetSearchKey]);

  const { data: filterResult = [], isLoading: isFilterLoading } = useAttendeeFilterResult({
    status,
    eventId,
    breakoutId,
    config: {
      enabled: isOpened && navigator.onLine,
    },
  });

  const { data: searchResult = [], isLoading: isSearchLoading } = useAttendeeSearchResult({
    eventId,
    breakoutId: breakoutId ?? undefined,
    status,
    searchKey: debouncedSearchKey,
    config: {
      enabled: isOpened && debouncedSearchKey.length > 0 && navigator.onLine,
    },
  });

  const activeResult = debouncedSearchKey.length > 0 ? searchResult : filterResult.slice(0, 50);

  const sortedSearchResult = [...activeResult].sort((a, b) => {
    const fieldA = (a[sortBy] ?? '').toString().toLowerCase();
    const fieldB = (b[sortBy] ?? '').toString().toLowerCase();

    if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const isLoading = isFilterLoading || isSearchLoading;

  return (
    <Dialog
      onOpenChange={isOpen => {
        setIsOpened(isOpen);
      }}
    >
      <Tooltip>
        <TooltipTrigger className="w-full">
          <DialogTrigger className="w-full" onClick={() => setIsOpened(true)}>
            {children}
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{getLabelValue('FilterByStatTooltip')}</p>
        </TooltipContent>
      </Tooltip>

      <DialogContent className="max-w-4xl w-full bg-card">
        <DialogHeader>
          <DialogTitle>{getLabelValue(status + 'DialogTitle')}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute top-1/2 left-3 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-8"
            onChange={e => {
              const value = e.target.value;
              setSearchKey(value);
              debouncedSetSearchKey(value);
            }}
            value={searchKey}
          />
        </div>

        <SortAttendees sortBy={sortBy} setSortBy={setSortBy} sortDirection={sortDirection} setSortDirection={setSortDirection} />

        <ScrollArea className="max-h-[calc(80vh)] min-h-[calc(80vh)]">
          <div className="space-y-2 w-[calc(100%_-_20px)]">
            {isLoading ? (
              <>
                <Skeleton className="w-full h-[65px]" />
                <Skeleton className="w-full h-[65px]" />
                <Skeleton className="w-full h-[65px]" />
                <Skeleton className="w-full h-[65px]" />
                <Skeleton className="w-full h-[65px]" />
              </>
            ) : (
              sortedSearchResult?.map((searchResult: AttendeeSearchResult) => (
                <SearchResultItem key={searchResult.badgeNo} searchResult={searchResult} proccessingBadgeNo={proccessingBadgeNo} onPassing={pass} status={status} />
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
